import { useState, useEffect } from "react";
import { Star, Upload, CheckCircle, AlertCircle, ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";
import { useParams, Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { id: "process", label: "Prozessoptimierung" },
  { id: "digital", label: "Digitalisierung" },
  { id: "ai", label: "KI & Automatisierung" },
  { id: "web", label: "Web & Digital Marketing" },
  { id: "software", label: "Software Entwicklung" },
];

export default function TestimonialSubmitPage() {
  const { token } = useParams();
  const [status, setStatus] = useState("loading");
  const [clientInfo, setClientInfo] = useState(null);
  const [form, setForm] = useState({
    client_name: "",
    company: "",
    position: "",
    text: "",
    rating: 5,
    category: "",
    photo_url: "",
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/testimonials/submit/${token}`);
        setClientInfo(data);
        setForm((f) => ({ ...f, client_name: data.client_name }));
        setStatus("ready");
      } catch (e) {
        const code = e.response?.status;
        if (code === 410) setStatus("expired");
        else if (code === 409) setStatus("already");
        else setStatus("invalid");
      }
    })();
  }, [token]);

  const handlePhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/testimonials/upload-photo`, fd);
      setForm((f) => ({ ...f, photo_url: data.url }));
    } catch {
      alert("Foto-Upload fehlgeschlagen");
    }
    setUploading(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.text.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/testimonials/submit/${token}`, form);
      setStatus("success");
    } catch {
      alert("Fehler beim Einreichen");
    }
    setSubmitting(false);
  };

  if (status === "loading") {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4A373] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (status === "expired" || status === "invalid" || status === "already") {
    const msgs = {
      expired: { title: "Link abgelaufen", desc: "Dieser Einreichungslink ist nicht mehr gültig. Bitte kontaktieren Sie LAB336 für einen neuen Link." },
      invalid: { title: "Ungültiger Link", desc: "Dieser Link ist nicht gültig. Bitte prüfen Sie die URL oder kontaktieren Sie LAB336." },
      already: { title: "Bereits eingereicht", desc: "Vielen Dank — Ihr Testimonial wurde bereits erfolgreich eingereicht." },
    };
    const m = msgs[status];
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center px-4">
        <div className="max-w-md text-center" data-testid="testimonial-error">
          <AlertCircle className="w-12 h-12 text-[#D4A373] mx-auto mb-4" />
          <h1 className="font-['Playfair_Display'] text-2xl font-semibold text-[#0F172A] mb-3">{m.title}</h1>
          <p className="text-[#64748B] mb-6">{m.desc}</p>
          <Link to="/" className="text-[#D4A373] text-sm font-medium hover:underline">Zur Startseite</Link>
        </div>
      </div>
    );
  }

  if (status === "success") {
    return (
      <div className="min-h-screen bg-[#F8F9FA] flex items-center justify-center px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md text-center" data-testid="testimonial-success">
          <CheckCircle className="w-16 h-16 text-[#10B981] mx-auto mb-4" />
          <h1 className="font-['Playfair_Display'] text-3xl font-semibold text-[#0F172A] mb-3">Vielen Dank!</h1>
          <p className="text-[#64748B] mb-6">Ihr Testimonial wurde erfolgreich eingereicht. Wir prüfen es und veröffentlichen es in Kürze auf unserer Website.</p>
          <Link to="/" className="inline-block bg-[#D4A373] text-[#0F172A] font-medium px-8 py-3 text-sm hover:bg-[#C4956A] transition-colors">Zur Startseite</Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA] py-12 px-4">
      <div className="max-w-xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#64748B] hover:text-[#0F172A] mb-8 transition-colors" data-testid="testimonial-back">
          <ArrowLeft className="w-4 h-4" />Zur Startseite
        </Link>

        {/* Header */}
        <div className="mb-8">
          <p className="text-[10px] font-semibold tracking-[0.2em] uppercase text-[#D4A373] mb-3">Feedback einreichen</p>
          <h1 className="font-['Playfair_Display'] text-3xl font-semibold text-[#0F172A] mb-3">
            Teilen Sie Ihre Erfahrung
          </h1>
          <p className="text-[#64748B] text-[15px] leading-relaxed">
            Ihre Meinung ist uns wichtig. Beschreiben Sie in wenigen Sätzen, wie die Zusammenarbeit mit LAB336 war.
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white border border-[#e2e5e9] divide-y divide-[#e9ecef]" data-testid="testimonial-form">
          {/* Name & Company */}
          <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-semibold tracking-[0.15em] uppercase text-[#94a3b8] mb-2">Name *</label>
              <input type="text" value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                className="w-full px-3 py-2.5 border border-[#e2e5e9] text-sm text-[#0F172A] focus:outline-none focus:border-[#D4A373]" required data-testid="testimonial-name" />
            </div>
            <div>
              <label className="block text-[10px] font-semibold tracking-[0.15em] uppercase text-[#94a3b8] mb-2">Firma</label>
              <input type="text" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })}
                className="w-full px-3 py-2.5 border border-[#e2e5e9] text-sm text-[#0F172A] focus:outline-none focus:border-[#D4A373]" data-testid="testimonial-company" />
            </div>
          </div>

          {/* Position */}
          <div className="p-6">
            <label className="block text-[10px] font-semibold tracking-[0.15em] uppercase text-[#94a3b8] mb-2">Position / Rolle</label>
            <input type="text" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} placeholder="z.B. CEO, Projektleiter, CTO"
              className="w-full px-3 py-2.5 border border-[#e2e5e9] text-sm text-[#0F172A] focus:outline-none focus:border-[#D4A373]" data-testid="testimonial-position" />
          </div>

          {/* Category */}
          <div className="p-6">
            <label className="block text-[10px] font-semibold tracking-[0.15em] uppercase text-[#94a3b8] mb-3">Bereich</label>
            <div className="flex flex-wrap gap-2">
              {CATEGORIES.map((c) => (
                <button key={c.id} type="button" onClick={() => setForm({ ...form, category: c.id })}
                  className={`px-3 py-1.5 text-xs font-medium border transition-colors ${form.category === c.id ? "bg-[#0F172A] text-[#D4A373] border-[#0F172A]" : "border-[#e2e5e9] text-[#64748B] hover:border-[#D4A373]"}`}
                  data-testid={`testimonial-cat-${c.id}`}>
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* Rating */}
          <div className="p-6">
            <label className="block text-[10px] font-semibold tracking-[0.15em] uppercase text-[#94a3b8] mb-3">Bewertung</label>
            <div className="flex gap-1" data-testid="testimonial-rating">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" onClick={() => setForm({ ...form, rating: n })}
                  className="p-1 transition-colors">
                  <Star className={`w-7 h-7 ${n <= form.rating ? "fill-[#D4A373] text-[#D4A373]" : "text-[#e2e5e9]"}`} />
                </button>
              ))}
            </div>
          </div>

          {/* Text */}
          <div className="p-6">
            <label className="block text-[10px] font-semibold tracking-[0.15em] uppercase text-[#94a3b8] mb-2">Ihr Feedback *</label>
            <textarea value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })}
              rows={5} placeholder="Beschreiben Sie Ihre Erfahrung mit LAB336..."
              className="w-full px-3 py-2.5 border border-[#e2e5e9] text-sm text-[#0F172A] leading-relaxed focus:outline-none focus:border-[#D4A373] resize-none"
              required data-testid="testimonial-text" />
          </div>

          {/* Photo */}
          <div className="p-6">
            <label className="block text-[10px] font-semibold tracking-[0.15em] uppercase text-[#94a3b8] mb-3">Foto (optional)</label>
            {form.photo_url ? (
              <div className="flex items-center gap-4">
                <img src={form.photo_url} alt="Preview" className="w-16 h-16 rounded-full object-cover border-2 border-[#D4A373]" />
                <button type="button" onClick={() => setForm({ ...form, photo_url: "" })} className="text-xs text-[#64748B] hover:text-red-500">Entfernen</button>
              </div>
            ) : (
              <label className="flex items-center gap-3 px-4 py-3 border border-dashed border-[#CBD5E1] cursor-pointer hover:border-[#D4A373] transition-colors" data-testid="testimonial-photo-upload">
                <Upload className={`w-5 h-5 ${uploading ? "animate-pulse text-[#D4A373]" : "text-[#94a3b8]"}`} />
                <span className="text-sm text-[#64748B]">{uploading ? "Wird hochgeladen..." : "Profilbild hochladen"}</span>
                <input type="file" accept="image/*" onChange={handlePhoto} className="hidden" />
              </label>
            )}
          </div>

          {/* Submit */}
          <div className="p-6 bg-[#F8F9FA]">
            <button type="submit" disabled={submitting || !form.text.trim()}
              className="w-full bg-[#D4A373] text-[#0F172A] font-medium py-3.5 text-sm tracking-wide hover:bg-[#C4956A] transition-colors disabled:opacity-50"
              data-testid="testimonial-submit-btn">
              {submitting ? "Wird eingereicht..." : "Testimonial einreichen"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
